import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Group, List, Loader, Modal, NumberInput, SegmentedControl, Select, SimpleGrid, Stack, Table, Text, TextInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ApiError, offerProjectApi, type Offer, type ProjectFromOfferResult, type ProjectPart } from '../../api';
import { PROJECT_FROM_OFFER_PARTS, formatDate, formatEuro } from '../../format';

/** The Monday after today, as a first working day to start from. */
function nextMonday(): string {
  const d = new Date();
  d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7));
  // Local date: toISOString would give the UTC day, one early late in the evening.
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * A project from the accepted offer (AP-98): what it takes over, shown as a
 * proposal before anything is made - the person chooses the variant, the
 * parts, the start and how many hours a day each role works on it.
 */
export function ProjectFromOfferModal({ offer, onClose }: { offer: Offer; onClose: () => void }): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [number, setNumber] = useState(offer.code);
  const [name, setName] = useState(offer.name);
  const [variantId, setVariantId] = useState(offer.variants[0]!.id);
  const variant = offer.variants.find((v) => v.id === variantId) ?? offer.variants[0]!;
  const [parts, setParts] = useState<Set<ProjectPart>>(new Set(['projectdata', 'time', 'schedule', 'costs']));
  const [start, setStart] = useState(nextMonday());
  const [capacity, setCapacity] = useState<Record<string, number>>({});
  const [sequence, setSequence] = useState<'chain' | 'parallel'>('chain');
  const [foreignDays, setForeignDays] = useState(10);
  const [costDepth, setCostDepth] = useState<1 | 2 | 3>(2);
  const [result, setResult] = useState<ProjectFromOfferResult | null>(null);
  // Default: every role four hours a day on this project.
  useEffect(() => { setCapacity((c) => Object.fromEntries(variant.data.roles.map((r) => [r.short, c[r.short] ?? 4]))); }, [variant.id]);
  const options = { start, capacity, sequence, foreignDays, costDepth };
  const [debounced] = useDebouncedValue({ variantId, options }, 400);
  const proposal = useQuery({
    queryKey: ['project-proposal', offer.id, debounced],
    queryFn: () => offerProjectApi.proposal(offer.id, debounced.variantId, debounced.options),
    enabled: /^\d{4}-\d{2}-\d{2}$/.test(debounced.options.start),
  });
  const create = useMutation({
    mutationFn: () => offerProjectApi.create(offer.id, { number: number.trim(), name: name.trim(), variantId, parts: [...parts], options }),
    onSuccess: async (r) => {
      setResult(r);
      await Promise.all([queryClient.invalidateQueries({ queryKey: ['projects'] }), queryClient.invalidateQueries({ queryKey: ['offer', offer.id] }), queryClient.invalidateQueries({ queryKey: ['offers'] })]);
    },
  });
  const toggle = (k: ProjectPart, on: boolean): void => setParts((s) => { const n = new Set(s); if (on) n.add(k); else n.delete(k); return n; });
  const p = proposal.data;

  if (result) {
    return (
      <Modal opened onClose={onClose} title="Projekt angelegt" size="lg">
        <Stack gap="sm">
          <List size="sm" spacing={4}>
            {result.report.map((r) => (
              <List.Item key={r.name} c={r.ok ? undefined : 'red.7'}><b>{r.name}</b>: {r.ok ? r.detail ?? 'übernommen' : `nicht angelegt – ${r.detail ?? 'Fehler'}`}</List.Item>
            ))}
          </List>
          {result.notes.length > 0 && <Alert color="yellow" py={6} title="Bitte prüfen">{result.notes.map((n) => <Text key={n} size="xs">{n}</Text>)}</Alert>}
          {result.report.some((r) => !r.ok) && <Text size="xs" c="dimmed">Fehlende Teile lassen sich im Projekt nachtragen, etwa über den Import einer PST-Import-Datei.</Text>}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Beim Angebot bleiben</Button>
            <Button onClick={() => navigate(`/projekte/${result.projectId}/termine`)}>Zum Projekt</Button>
          </Group>
        </Stack>
      </Modal>
    );
  }

  return (
    <Modal opened onClose={onClose} title="Projekt aus dem Angebot anlegen" size="xl">
      <Stack gap="sm">
        <Text size="sm">Legt ein neues Projekt an und übernimmt, was das Angebot hergibt – vorher steht hier, was entsteht. Das Angebot wird auf „beauftragt“ gesetzt und bleibt mit dem Projekt verbunden.</Text>
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
          <TextInput label="Projektnummer" value={number} onChange={(e) => setNumber(e.currentTarget.value)} maxLength={40} />
          <TextInput label="Projektname" value={name} onChange={(e) => setName(e.currentTarget.value)} maxLength={200} style={{ gridColumn: 'span 2' }} />
        </SimpleGrid>
        <Select label="Variante" value={variant.id} onChange={(v) => v && setVariantId(v)} allowDeselect={false} comboboxProps={{ withinPortal: true }}
          data={offer.variants.map((v) => ({ value: v.id, label: `${v.code} · ${v.name}` }))} />
        <Stack gap={4}>
          <Text size="sm" fw={500}>Übernehmen</Text>
          {(Object.keys(PROJECT_FROM_OFFER_PARTS) as ProjectPart[]).map((k) => (
            <Checkbox key={k} checked={parts.has(k)} onChange={(e) => toggle(k, e.currentTarget.checked)} label={PROJECT_FROM_OFFER_PARTS[k].label} description={PROJECT_FROM_OFFER_PARTS[k].hint} />
          ))}
        </Stack>
        {parts.has('schedule') && (
          <Stack gap="xs">
            <Group gap="sm" align="flex-end" wrap="wrap">
              <TextInput label="Beginn" type="date" value={start} onChange={(e) => setStart(e.currentTarget.value)} w={170} />
              <SegmentedControl value={sequence} onChange={(v) => setSequence(v as 'chain' | 'parallel')} data={[{ value: 'chain', label: 'nacheinander' }, { value: 'parallel', label: 'gleichzeitig' }]} />
              <NumberInput label="Tage für Nachunternehmer" description="Mindestdauer einer Position mit Anteil eines Nachunternehmers" value={foreignDays} min={1} max={500} onChange={(v) => setForeignDays(Number(v) || 1)} w={200} />
            </Group>
            <Group gap="sm" wrap="wrap">
              {variant.data.roles.map((r) => (
                <NumberInput key={r.short} label={`${r.short} ${r.name}`} description="Stunden je Arbeitstag" value={capacity[r.short] ?? 4} min={0} max={24} step={0.5} decimalScale={1}
                  onChange={(v) => setCapacity((c) => ({ ...c, [r.short]: Number(v) || 0 }))} w={180} />
              ))}
            </Group>
          </Stack>
        )}
        {parts.has('costs') && (
          <Group gap="xs"><Text size="sm">Kostengruppen nach DIN 276 bis Ebene</Text>
            <SegmentedControl size="xs" value={String(costDepth)} onChange={(v) => setCostDepth(Number(v) as 1 | 2 | 3)} data={['1', '2', '3']} />
          </Group>
        )}
        {proposal.isPending && <Loader size="sm" />}
        {proposal.isError && <Alert color="red" py={6}>{proposal.error instanceof ApiError ? proposal.error.message : 'Der Vorschlag ließ sich nicht berechnen.'}</Alert>}
        {p && (
          <Stack gap={6}>
            <Text size="sm" fw={500}>Vorschlag</Text>
            <Text size="xs">
              Angebotssumme {formatEuro(p.project.offerNet ?? 0)} netto · {p.contacts.length} Kontakte · {p.time.categories.length} Kategorien der Zeiterfassung
              {p.project.bindingUntil ? ` · Bindefrist bis ${formatDate(p.project.bindingUntil)}` : ''}
            </Text>
            {parts.has('schedule') && p.schedule.tasks && p.schedule.tasks.length > 0 && (
              <Table verticalSpacing={2} withTableBorder>
                <Table.Thead><Table.Tr><Table.Th w={60}>Nr.</Table.Th><Table.Th>Vorgang</Table.Th><Table.Th w={110} ta="right">Arbeitstage</Table.Th><Table.Th w={90}>Verantw.</Table.Th><Table.Th w={110}>nach</Table.Th></Table.Tr></Table.Thead>
                <Table.Tbody>
                  {p.schedule.tasks.map((t) => (
                    <Table.Tr key={t.code}>
                      <Table.Td><Text size="xs" ff="monospace">{t.code}</Text></Table.Td>
                      <Table.Td><Text size="xs">{t.name}</Text></Table.Td>
                      <Table.Td ta="right"><Text size="xs">{t.durationDays}</Text></Table.Td>
                      <Table.Td><Text size="xs">{t.owner}</Text></Table.Td>
                      <Table.Td><Text size="xs">{t.predecessors?.map((x) => (typeof x === 'string' ? x : x.code)).join(', ') ?? (t.start ? `ab ${formatDate(t.start)}` : '')}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
            {p.notes.length > 0 && <Alert color="yellow" py={6}>{p.notes.map((n) => <Text key={n} size="xs">{n}</Text>)}</Alert>}
          </Stack>
        )}
        {create.isError && <Alert color="red" py={6}>{create.error instanceof ApiError ? create.error.message : 'Das Projekt ließ sich nicht anlegen.'}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Abbrechen</Button>
          <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!number.trim() || !name.trim() || !p}>Projekt anlegen</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
